import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { Payment } from 'src/features/payments/utils/types';
import { paymentFormSchema, PaymentFormSchemaValues } from 'src/features/payments/utils/formSchema';
import { useGetPayment } from 'src/features/payments/api/useGetPayment';
import PaymentForm from 'src/features/payments/components/forms/PaymentForm';
import { useParams } from 'react-router-dom';

interface PaymentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPayment?: Payment;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (data: PaymentFormSchemaValues, onSuccess: () => void, onError: (error: GeneralASError) => void) => void;
}

function PaymentFormModal<TData>({
    selectedPayment,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: PaymentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { customerId = '' } = useParams();

    const { data: paymentData } = useGetPayment({
        paymentId: selectedPayment?.id ?? '',
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedPayment?.id && isModalOpen },
    });

    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            date: paymentData?.date ?? undefined,
            currency: paymentData?.currency ?? undefined,
            amount: paymentData?.amount ?? undefined,
            type: paymentData?.type ?? undefined,
            reference: paymentData?.reference ?? undefined,
            customer: paymentData?.customer?.id ?? customerId ?? undefined,
            money_account: paymentData?.money_account?.id ?? undefined,
        };
    }, [paymentData]);

    const form = useForm<PaymentFormSchemaValues>({
        resolver: zodResolver(paymentFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: PaymentFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <PaymentForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedPayment && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default PaymentFormModal;
